import "./RequestCard.css";
import React, { useState, useEffect } from "react";
import { formatMeetingDateTime, toIsoDate } from "../utils/dateHelpers";
import { getEventColorForDate, getColorWithOpacity } from "../utils/eventColorHelper";

const RequestCard = ({ request, onClick, onTakeClick, onAskEdit, style, variant = "default", compact = false, isNextMeeting = false }) => {
  const [eventColor, setEventColor] = useState(null);
  const [showAddress, setShowAddress] = useState(false);
  const [expanded, setExpanded] = useState(variant !== "pending");
  const name = request.name || request.passengerName;
  
  // Debug: log pour voir si les demandes arrivent
  console.log('🔍 RequestCard render:', { name, variant, requestId: request.id, icon: request.icon });
  const created = request.createdAt ? new Date(request.createdAt) : null;
  const createdLabel = created
    ? `${String(created.getDate()).padStart(2, "0")}/${String(created.getMonth() + 1).padStart(2, "0")} à ${String(created.getHours()).padStart(2, "0")}:${String(created.getMinutes()).padStart(2, "0")}`
    : "";

  // Utilitaires d'adresse
  const sanitizeAddress = (addr = "", city = "") => {
    // Supprime un code postal (5 chiffres) et virgules superflues, trim espaces
    let a = String(addr || "");
    a = a.replace(/\b\d{5}\b/g, "");
    if (city) {
      // Évite la répétition de la ville dans l'adresse
      const cityEsc = city.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
      a = a.replace(new RegExp(`,?\\s*${cityEsc}\\s*,?`, "i"), "");
    }
    a = a.replace(/\s{2,}/g, " ").replace(/\s*,\s*/g, ", ").trim().replace(/^,\s*/, "");
    return a;
  };

  // Adresse complète (pour Google Maps / modale)
  const fullAddress = [request.address, request.postalCode, request.city]
    .filter(Boolean)
    .join(", ");
  // Adresse à afficher (pending): voie + ville uniquement, sans CP, sans doublons
  const cleanedStreet = sanitizeAddress(request.address, request.city);
  const displayAddress = [cleanedStreet, request.city].filter(Boolean).join(", ");

  const hasReturn = Boolean(
    request.returnDifferent &&
      (request.returnAddress || request.returnCity || request.returnPostalCode),
  );
  const cleanedReturnStreet = sanitizeAddress(request.returnAddress, request.returnCity);
  const displayReturnAddress = [cleanedReturnStreet, request.returnCity]
    .filter(Boolean)
    .join(", ");

  // Afficher le bouton Modifier si la demande n'est pas assignée
  const canEdit = request.assigned !== true && onAskEdit;

  const handleEdit = (e) => {
    e.stopPropagation();
    if (onAskEdit) {
      onAskEdit(request);
    }
  };

  const remainingSeats = Number(
    request.seatsRemaining ?? (request.seatsNeeded || 1) - (request.seatsAssigned || 0),
  );

  const handleToggle = () => {
    if (variant === "pending") setExpanded((v) => !v);
  };

  const handleHeaderKeyDown = (e) => {
    if (variant !== "pending") return;
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      setExpanded((v) => !v);
    }
  };

  // Récupérer la couleur de l'événement si la demande est liée à un événement
  useEffect(() => {
    const loadEventColor = async () => {
      if (request.meetingAt) {
        const isoDate = request.isoDate || toIsoDate(request.meetingAt);
        const color = await getEventColorForDate(isoDate);
        setEventColor(color);
      }
    };
    loadEventColor();
  }, [request.meetingAt, request.isoDate]);

  return (
    <article
      aria-label={`Demande de ${name}`}
      onClick={onClick || handleToggle}
      className={`animate-fade-in-up request-card ${variant !== "default" ? `req-card-${variant}` : ""}`}
      style={{ ...(style || {}), ...(variant === "pending" ? { background: "transparent", border: "none", boxShadow: "none", padding: 0 } : {}) }}
    >
      {variant === "pending" && (
        <div
          className="request-card__meta"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            padding: '12px 16px',
            cursor: 'pointer',
            borderRadius: 14,
            border: '1px solid #e5e7eb',
            background: '#fff',
            boxShadow: '0 1px 2px rgba(0,0,0,.06)'
          }}
          role="button"
          aria-expanded={expanded}
          tabIndex={0}
          onKeyDown={handleHeaderKeyDown}
          onClick={(e) => { e.stopPropagation(); handleToggle(); }}
        >
          <div aria-hidden style={{ fontSize: 20, color:'#1f2937', transform: `rotate(${expanded ? 90 : 0}deg)`, transition: 'transform .2s' }}>▶</div>
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, lineHeight: 1.4 }}>
            <div className="req-name" style={{ fontWeight: 700, fontSize: 16, color: '#111827', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis', display:'flex', alignItems:'center', gap: 6 }}>
              <span style={{ fontSize: 18 }}>{request.icon || '👤'}</span>
              <span>{name}</span>
            </div>
            {request.meetingAt && (
              <div style={{ fontSize: 12, color:'#4b5563', marginTop: 2, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>
                {formatMeetingDateTime(request.meetingAt)}
              </div>
            )}
            {/* Badge des places en dessous du nom */}
            {(() => {
              const remaining = Math.max(0, remainingSeats);
              const isPending = variant === 'pending';
              let alert = false;
              if (isPending && request.meetingAt) {
                const DAY = 24*60*60*1000; const now = Date.now(); const ts = Number(request.meetingAt);
                const d = new Date(ts); const sod = new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
                const eveStart = sod - DAY; const inEveWindow = now >= eveStart && now < ts;
                alert = (inEveWindow || isNextMeeting) && remaining > 0;
              }
              const baseStyle = {
                fontWeight: 600,
                borderRadius: 12,
                padding: '2px 6px',
                display: 'inline-block',
                fontSize: '11px',
                textAlign: 'center',
                marginTop: 4,
                alignSelf: 'flex-start'
              };
              const normalStyle = { background:'#E5EDFF', color:'#1D4ED8' };
              const alertStyle = { background:'#fee2e2', color:'#b91c1c', border:'1px solid #fecaca' };
              // Éviter "1ère place" en utilisant un format explicite
              const placeText = remaining === 1 ? '1 place' : `${remaining} places`;
              return (
                <span style={{ ...baseStyle, ...(alert ? alertStyle : normalStyle) }}>
                  {alert ? '⚠️ ' : ''}{placeText}
                </span>
              );
            })()}
          </div>
        </div>
      )}

      {expanded && (
      <div className="req-line request-card__info" style={{ display:'grid', ...(compact ? { gap: 10 } : {}), padding: '12px 14px', background: '#fff', border:'1px solid #e5e7eb', borderRadius: 14, marginTop: 8 }}>
        {/* PENDING: adresse tout en haut */}
        {variant === 'pending' && (
          <div style={{ marginBottom: 10 }}>
            {displayAddress && (
              <div className="request-card__address" style={{ fontSize: 16, lineHeight: 1.45, textAlign:'left' }}>
                {hasReturn ? (
                  <>
                    <div><strong>Aller :</strong> {displayAddress}</div>
                    {displayReturnAddress && (
                      <div style={{ marginTop: 6 }}><strong>Retour :</strong> {displayReturnAddress}</div>
                    )}
                  </>
                ) : (
                  displayAddress
                )}
              </div>
            )}
          </div>
        )}

        {/* NON-PENDING: avatar + deux colonnes */}
        {variant !== "pending" ? (
          <>
            <div className="req-avatar" aria-hidden>{request.icon || '👤'}</div>
            <div style={{ display:'grid', gridTemplateColumns: '1fr 1fr', gap: 12, width:'100%' }}>
              <div>
                <div className="req-name" style={{ fontWeight: 700, fontSize: compact ? 16 : 18, lineHeight: 1.4 }}>{name}</div>
                {created && (
                  <div className="req-created" style={{ lineHeight: 1.5 }}>Créée le {createdLabel}</div>
                )}
              </div>
              <div>
                {fullAddress && (
                  <div className="request-card__address" style={{ fontSize: 16, lineHeight: 1.45 }}>{fullAddress}</div>
                )}
              </div>
            </div>
          </>
        ) : null}

        {/* Boutons d'action (toujours en dessous) */}
        <div className="request-card__actions" style={{ gap: 8, gridTemplateColumns: '1fr 1fr' }}>
          {canEdit && (
            <button
              type="button"
              className="request-card__btn btn-secondary"
              onClick={handleEdit}
              title="Modifier cette demande"
              aria-label="Modifier cette demande"
            >
              ✏️ Modifier
            </button>
          )}
          {onTakeClick && (
            <button
              type="button"
              className="request-card__btn btn-primary"
              onClick={(e) => {
                e.stopPropagation();
                onTakeClick(request);
              }}
            >
              ✅ Je le prends
            </button>
          )}
        </div>
      </div>
      )}

      {showAddress && (
        <div className="simple-modal" role="dialog" aria-modal="true" onClick={() => setShowAddress(false)}>
          <div className="simple-modal__card" onClick={(e) => e.stopPropagation()}>
            <h4 style={{ margin: 0, marginBottom: 8 }}>Adresse complète</h4>
            <div style={{ fontSize: 16, lineHeight: 1.4, marginBottom: 12 }}>{fullAddress}</div>
            {fullAddress && (
              <a
                className="request-card__btn btn-primary"
                href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(fullAddress)}`}
                target="_blank" rel="noreferrer"
                style={{ display: 'inline-block', textAlign: 'center' }}
              >
                Ouvrir dans Google Maps
              </a>
            )}
            <button className="request-card__btn btn-secondary" onClick={() => setShowAddress(false)} style={{ marginTop: 8 }}>Fermer</button>
          </div>
        </div>
      )}
    </article>
  );
};

export default RequestCard;
